import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    Box,
    CardActionArea,
    CardContent,
    Typography,
    capitalize,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
    PatchPayStatePayload,
    PayStateT,
} from 'routes/HumanResourcesModule/PayState/helpers/types';
import PayStateItemTitle from 'routes/HumanResourcesModule/PayState/PayStateHome/components/PayStateItem/PayStateItemTitle';
import {
    useDeletePayState,
    usePatchPayState,
} from 'routes/HumanResourcesModule/PayState/helpers/hooks/payState';
import PayStateModal from 'routes/HumanResourcesModule/PayState/PayStateHome/components/PayStateModal/PayStateModal';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';

const StyCardContent = styled(CardContent)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    height: '100%',
}));

const PayStateTitleWrapper = styled(Box)({
    flex: 1,
    maxHeight: '50%',
});

type PayStateItemProps = {
    payState: PayStateT;
};

const PayStateItem = ({ payState }: PayStateItemProps): React.ReactElement => {
    const { t } = useTranslation();
    const [showPayStateModal, setShowPayStateModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const updatePayStateMutation = usePatchPayState();
    const deleteInscriptionMutation = useDeletePayState(
        payState.id,
        payState.exercice,
    );
    const { isPending: isDeletePayStateLoading } = deleteInscriptionMutation;
    const { isError: isUpdatePayStateError } = updatePayStateMutation;

    const onSave = ({ exercice, title }: PatchPayStatePayload) => {
        updatePayStateMutation.mutate({
            id: payState.id,
            exercice,
            title,
        });
    };

    const togglePayStateModal = useCallback(() => {
        setShowPayStateModal((current) => !current);
    }, []);

    const toggleDeletePayStateModal = useCallback(() => {
        setShowConfirmDeleteModal((current) => !current);
    }, []);

    const onEditButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        togglePayStateModal();
    };

    const onDeleteButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        toggleDeletePayStateModal();
    };
    const deletePayState = () => {
        deleteInscriptionMutation.mutate();
    };
    const formattedDate = capitalize(
        new Date(payState.createdAt).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        }),
    );
    return (
        <>
            <Link to={`/human_resources/pay_state/${payState.id}/diary`}>
                <StyCard sx={{ height: '12rem' }}>
                    <CardActionArea sx={{ height: '100%' }} component="span">
                        <StyCardContent>
                            <PayStateTitleWrapper>
                                <PayStateItemTitle
                                    payState={payState}
                                    onEditButtonClick={onEditButtonClick}
                                    onDeleteButtonClick={onDeleteButtonClick}
                                />
                                <Box paddingTop="0.8rem">
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                        display="inline"
                                    >
                                        {t('humanResources.creationDate')}
                                    </Typography>
                                    &nbsp;&nbsp;
                                    {formattedDate}
                                </Box>
                            </PayStateTitleWrapper>
                        </StyCardContent>
                    </CardActionArea>
                </StyCard>
            </Link>
            {showPayStateModal && (
                <PayStateModal
                    onSave={onSave}
                    editMode
                    show={showPayStateModal}
                    onClose={togglePayStateModal}
                    payState={payState}
                    isLoading={isDeletePayStateLoading}
                    isError={isUpdatePayStateError}
                    currentExercice={payState.exercice}
                />
            )}
            {showConfirmDeleteModal && (
                <ConfirmDeleteModal
                    title={t('humanResources.deletePayState')}
                    confirm={deletePayState}
                    isLoading={isDeletePayStateLoading}
                    show={showConfirmDeleteModal}
                    cancel={toggleDeletePayStateModal}
                />
            )}
        </>
    );
};

export default PayStateItem;
